import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, Image as RNImage, ActivityIndicator, Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import WebScrollView from '../components/WebScrollView';
import { supabase } from '../lib/supabase';
import { ChevronDown, Home as HomeIcon, Calendar, DollarSign, Wrench, Package, CheckCircle2, Image as ImageIcon, FileText, Gavel, Tag, Landmark, Info, Edit, Check, X } from '../lib/icons';
import { createShadow } from '../lib/styles';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  square_footage?: number;
  year_built?: number;
  purchase_date?: string;
  cover_photo_url?: string;
  bedrooms?: number;
  bathrooms?: number;
  created_at: string;
}

interface TimelineEvent {
  id: string;
  type: 'service_request' | 'visit' | 'expense' | 'maintenance' | 'inventory' | 'property_record';
  title: string;
  description?: string;
  date: string;
  amount?: number;
  status?: string;
  icon: any;
  expenseTotal?: number;
  recordType?: 'permit' | 'tax_assessment' | 'sale' | 'lien' | 'other';
}

const HomeDetail = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [homes, setHomes] = useState<Home[]>([]);
  const [selectedHome, setSelectedHome] = useState<Home | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHomeSwitcher, setShowHomeSwitcher] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Home>>({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(homesLoading);
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      const resolvedHome = (accessibleHomes as Home[]).find((home) => home.id === activeHome.id) || null;
      setSelectedHome(resolvedHome);
    } else if (!homesLoading) {
      setSelectedHome(null);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  useEffect(() => {
    if (selectedHome) {
      fetchTimelineEvents(selectedHome.id);
    }
  }, [selectedHome]);

  const fetchTimelineEvents = async (homeId: string) => {
    try {
      const events: TimelineEvent[] = [];

      // Fetch service requests
      const { data: serviceRequests } = await supabase
        .from('service_requests')
        .select('*')
        .eq('home_id', homeId)
        .order('created_at', { ascending: false });

      // Fetch service request expenses and aggregate by service_request_id
      const { data: serviceRequestExpenses } = await supabase
        .from('service_request_expenses')
        .select('service_request_id, amount, parent_expense_id')
        .in('service_request_id', serviceRequests?.map(sr => sr.id) || []);

      // Create a map of service request ID to total expenses
      const expenseTotals = new Map<string, number>();
      if (serviceRequestExpenses) {
        serviceRequestExpenses
          .filter(expense => !expense.parent_expense_id)
          .forEach(expense => {
          const currentTotal = expenseTotals.get(expense.service_request_id) || 0;
          expenseTotals.set(expense.service_request_id, currentTotal + Number(expense.amount));
          });
      }

      if (serviceRequests) {
        serviceRequests.forEach(sr => {
          events.push({
            id: sr.id,
            type: 'service_request',
            title: sr.title || 'Service Request',
            description: sr.description,
            date: sr.completed_at || sr.created_at,
            status: sr.status,
            icon: Wrench,
            expenseTotal: expenseTotals.get(sr.id) || undefined,
          });
        });
      }

      // Fetch standalone operational expenses (not tied to a service request)
      const { data: standaloneOperationalExpenses } = await supabase
        .from('service_request_expenses')
        .select('id, amount, description, category, expense_date, created_at, visit_id, parent_expense_id')
        .eq('home_id', homeId)
        .is('service_request_id', null)
        .is('parent_expense_id', null)
        .eq('billable_to_homeowner', true)
        .order('created_at', { ascending: false });

      const visitExpenseTotals = new Map<string, number>();
      if (standaloneOperationalExpenses) {
        standaloneOperationalExpenses
          .filter((expense) => expense.visit_id)
          .forEach((expense) => {
            const currentTotal = visitExpenseTotals.get(expense.visit_id!) || 0;
            visitExpenseTotals.set(expense.visit_id!, currentTotal + Number(expense.amount));
          });
      }

      // Fetch visits (including those without scheduled dates)
      const { data: visits } = await supabase
        .from('visits')
        .select('*')
        .eq('home_id', homeId)
        .order('scheduled_date', { ascending: false, nullsFirst: false });

      if (visits) {
        visits.forEach(visit => {
          // Use scheduled_date if available, otherwise use created_at for sorting
          // Visits without dates will be shown as "upcoming"
          const eventDate = visit.scheduled_date || visit.created_at;
          
          events.push({
            id: visit.id,
            type: 'visit',
            title: `${visit.visit_type === 'maintenance' ? 'Maintenance' : visit.visit_type === 'walkthrough' ? 'Walkthrough' : 'Service'} Visit`,
            description: visit.visit_notes,
            date: eventDate,
            status: visit.scheduled_date ? visit.status : 'upcoming',
            amount: visit.cost,
            expenseTotal: visitExpenseTotals.get(visit.id) || undefined,
            icon: Calendar,
          });
        });
      }

      if (standaloneOperationalExpenses) {
        standaloneOperationalExpenses
          .filter((expense) => !expense.visit_id)
          .forEach((expense) => {
            events.push({
              id: `operational-${expense.id}`,
              type: 'expense',
              title: 'Home Expense',
              description: expense.description || expense.category || undefined,
              date: expense.expense_date || expense.created_at,
              amount: expense.amount,
              icon: DollarSign,
            });
          });
      }

      // Fetch expenses
      const { data: expenses } = await supabase
        .from('expenses')
        .select('*')
        .eq('home_id', homeId)
        .order('date', { ascending: false });

      if (expenses) {
        expenses.forEach(expense => {
          events.push({
            id: expense.id,
            type: 'expense',
            title: expense.title,
            description: expense.description,
            date: expense.date,
            amount: expense.amount,
            icon: DollarSign,
          });
        });
      }

      // Fetch maintenance plans with completion details
      const { data: maintenancePlans } = await supabase
        .from('maintenance_plans')
        .select('*, maintenance_tasks(*)')
        .eq('home_id', homeId)
        .not('last_completed_date', 'is', null)
        .order('last_completed_date', { ascending: false });

      if (maintenancePlans) {
        maintenancePlans.forEach(plan => {
          // Get task name - use custom name if it's a custom task, otherwise use template name
          const taskName = plan.is_custom 
            ? plan.custom_task_name 
            : plan.maintenance_tasks?.name || 'Maintenance Task';

          // Build description with completion details
          let description = 'Completed';
          if (plan.completed_by) {
            const completedByText = plan.completed_by === 'homeowner' ? 'by homeowner' : 'by professional';
            description = `Completed ${completedByText}`;
          }
          if (plan.completion_notes) {
            description += plan.completed_by ? ` - ${plan.completion_notes}` : plan.completion_notes;
          }

          events.push({
            id: `${plan.id}-${plan.last_completed_date}`, // Unique ID for each completion
            type: 'maintenance',
            title: taskName,
            description: description,
            date: plan.last_completed_date,
            status: 'completed',
            icon: CheckCircle2,
          });
        });
      }

      // Fetch inventory items
      const { data: inventoryItems } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('home_id', homeId)
        .order('purchase_date', { ascending: false })
        .limit(10);

      if (inventoryItems) {
        inventoryItems.forEach(item => {
          if (item.purchase_date) {
            events.push({
              id: item.id,
              type: 'inventory',
              title: `Added ${item.name}`,
              description: item.category,
              date: item.purchase_date,
              amount: item.purchase_price,
              icon: Package,
            });
          }
        });
      }

      // Fetch property records
      const { data: propertyRecords } = await supabase
        .from('property_records')
        .select('*')
        .eq('home_id', homeId)
        .order('date', { ascending: false });

      if (propertyRecords) {
        propertyRecords.forEach(record => {
          let Icon = Info;
          switch (record.record_type) {
            case 'permit': Icon = FileText; break;
            case 'tax_assessment': Icon = Landmark; break;
            case 'sale': Icon = Tag; break;
            case 'lien': Icon = Gavel; break;
          }

          events.push({
            id: record.id,
            type: 'property_record',
            title: record.title,
            description: record.description,
            date: record.date,
            amount: record.amount,
            recordType: record.record_type,
            icon: Icon,
          });
        });
      }

      // Add home built event if year_built is available
      if (selectedHome?.year_built) {
        events.push({
          id: `home-built-${homeId}`,
          type: 'maintenance',
          title: 'Home Built',
          description: `This home was built in ${selectedHome.year_built}`,
          date: `${selectedHome.year_built}-01-01`, // Use January 1st of the build year
          icon: HomeIcon,
        });
      }

      // Add purchase date event if purchase_date is available
      if (selectedHome?.purchase_date) {
        events.push({
          id: `home-purchased-${homeId}`,
          type: 'property_record',
          title: 'Home Purchased',
          description: 'You purchased this home',
          date: selectedHome.purchase_date,
          icon: Calendar,
        });
      }

      // Add "Added to HoneyDone" celebratory event
      if (selectedHome?.created_at) {
        events.push({
          id: `home-added-${homeId}`,
          type: 'maintenance',
          title: 'Added to HoneyDone',
          description: 'This home was added to your HoneyDone account',
          date: selectedHome.created_at,
          icon: CheckCircle2,
        });
      }

      // Sort all events by date
      // Put "upcoming" visits (status === 'upcoming') first, then sort by date descending
      events.sort((a, b) => {
        const aIsUpcoming = a.status === 'upcoming';
        const bIsUpcoming = b.status === 'upcoming';
        
        if (aIsUpcoming && !bIsUpcoming) return -1;
        if (!aIsUpcoming && bIsUpcoming) return 1;
        
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      setTimelineEvents(events);
    } catch (error) {
      console.error('Error fetching timeline events:', error);
    }
  };

  const handleCoverPhotoClick = () => {
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      handleMobileImageUpload();
    }
  };

  const handleWebImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedHome) return;

    await uploadImage(file);
  };

  const handleMobileImageUpload = async () => {
    if (!selectedHome) return;

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'image/*',
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const file = result.assets[0];
      
      // For React Native, we need to convert the file to a blob
      const response = await fetch(file.uri);
      const blob = await response.blob();
      
      await uploadImage(blob, file.name);
    } catch (error) {
      console.error('Error picking image:', error);
      setUploadError('Failed to pick image');
    }
  };

  const uploadImage = async (file: File | Blob, fileName?: string) => {
    if (!selectedHome) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Generate file name
      const fileExt = fileName ? fileName.split('.').pop() : 'jpg';
      const generatedFileName = `${Math.random()}.${fileExt}`;
      const filePath = `images/${generatedFileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('homes')
        .upload(filePath, file);

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          throw new Error('Storage not configured. Please contact support.');
        }
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('homes')
        .getPublicUrl(filePath);

      // Update home in database
      const { error: updateError } = await supabase
        .from('homes')
        .update({ cover_photo_url: publicUrl })
        .eq('id', selectedHome.id);

      if (updateError) throw updateError;

      // Update local state
      setSelectedHome(prev => prev ? { ...prev, cover_photo_url: publicUrl } : null);
      setHomes(prev => prev.map(home => 
        home.id === selectedHome.id ? { ...home, cover_photo_url: publicUrl } : home
      ));

    } catch (error: any) {
      console.error('Error uploading image:', error);
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = () => {
    if (selectedHome) {
      setEditFormData({
        address: selectedHome.address,
        city: selectedHome.city,
        state: selectedHome.state,
        zip_code: selectedHome.zip_code,
        square_footage: selectedHome.square_footage,
        bedrooms: selectedHome.bedrooms,
        bathrooms: selectedHome.bathrooms,
        year_built: selectedHome.year_built,
      });
      setIsEditing(true);
      setUploadError(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
    setUploadError(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedHome) return;

    setSaving(true);
    setUploadError(null);

    try {
      // Prepare update data, converting empty strings to null for optional fields
      const updateData: any = {
        address: editFormData.address || null,
        city: editFormData.city || null,
        state: editFormData.state || null,
        zip_code: editFormData.zip_code || null,
      };

      // Handle numeric fields - convert empty strings to null
      if (editFormData.square_footage !== undefined) {
        const val = editFormData.square_footage;
        updateData.square_footage = (typeof val === 'string' && val === '') || val === null 
          ? null 
          : Number(val);
      }
      if (editFormData.bedrooms !== undefined) {
        const val = editFormData.bedrooms;
        updateData.bedrooms = (typeof val === 'string' && val === '') || val === null 
          ? null 
          : Number(val);
      }
      if (editFormData.bathrooms !== undefined) {
        const val = editFormData.bathrooms;
        updateData.bathrooms = (typeof val === 'string' && val === '') || val === null 
          ? null 
          : Number(val);
      }
      if (editFormData.year_built !== undefined) {
        const val = editFormData.year_built;
        updateData.year_built = (typeof val === 'string' && val === '') || val === null 
          ? null 
          : Number(val);
      }

      const { error } = await supabase
        .from('homes')
        .update(updateData)
        .eq('id', selectedHome.id);

      if (error) throw error;

      // Update local state
      const updatedHome = { ...selectedHome, ...updateData };
      setSelectedHome(updatedHome);
      setHomes(prev => prev.map(home => 
        home.id === selectedHome.id ? updatedHome : home
      ));

      setIsEditing(false);
      setEditFormData({});
    } catch (error: any) {
      console.error('Error saving home details:', error);
      setUploadError(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Home, value: string | number | null) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000000" />
      </View>
    );
  }

  if (!selectedHome) {
    return (
      <View style={styles.emptyContainer}>
        <HomeIcon size={48} color="#9ca3af" />
        <Text style={styles.emptyText}>No homes found</Text>
        <Text style={styles.emptySubtext}>Add a home to get started</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Home Switcher Dropdown */}
      {showHomeSwitcher && (
        <>
          <Pressable
            style={styles.switcherBackdrop}
            onPress={() => setShowHomeSwitcher(false)}
          />
          <View style={styles.switcherDropdown}>
            {homes.map(home => (
              <Pressable
                key={home.id}
                style={[
                  styles.switcherItem,
                  home.id === selectedHome.id && styles.switcherItemActive,
                ]}
                onPress={() => {
                  setSelectedHome(home);
                  setActiveHomeById(home.id);
                  setShowHomeSwitcher(false);
                }}
              >
                <Text style={[
                  styles.switcherItemText,
                  home.id === selectedHome.id && styles.switcherItemTextActive,
                ]}>
                  {home.address}
                </Text>
                <Text style={styles.switcherItemSubtext}>
                  {home.city}, {home.state}
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      )}

      <WebScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Cover Photo */}
        <View style={styles.coverPhotoContainer}>
          {selectedHome.cover_photo_url ? (
            <RNImage
              source={{ uri: selectedHome.cover_photo_url }}
              style={styles.coverPhoto}
              resizeMode="cover"
            />
          ) : (
            <View style={styles.coverPhotoPlaceholder}>
              <HomeIcon size={48} color="#9ca3af" />
            </View>
          )}
          
          {/* Upload Button Overlay */}
          <Pressable 
            style={styles.uploadButton}
            onPress={handleCoverPhotoClick}
            disabled={uploading}
          >
            {uploading ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <>
                <ImageIcon size={20} color="#ffffff" />
                <Text style={styles.uploadButtonText}>
                  {selectedHome.cover_photo_url ? 'Change Photo' : 'Add Photo'}
                </Text>
              </>
            )}
          </Pressable>

          {/* Hidden file input for web */}
          {Platform.OS === 'web' && (
            <input
              ref={fileInputRef as any}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleWebImageUpload as any}
            />
          )}
        </View>

        {/* Upload Error Message */}
        {uploadError && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{uploadError}</Text>
          </View>
        )}

        {/* Home Info & Switcher */}
        <View style={styles.homeInfoContainer}>
          <View style={styles.homeInfoHeader}>
            <Pressable
              style={styles.homeSwitcher}
              onPress={() => !isEditing && setShowHomeSwitcher(!showHomeSwitcher)}
              disabled={isEditing}
            >
              <View style={styles.homeSwitcherContent}>
                {isEditing ? (
                  <>
                    <TextInput
                      style={styles.editInput}
                      value={editFormData.address || ''}
                      onChangeText={(value) => handleInputChange('address', value)}
                      placeholder="Address"
                    />
                    <View style={styles.addressRow}>
                      <TextInput
                        style={[styles.editInput, styles.editInputSmall, { flex: 1 }]}
                        value={editFormData.city || ''}
                        onChangeText={(value) => handleInputChange('city', value)}
                        placeholder="City"
                      />
                      <TextInput
                        style={[styles.editInput, styles.editInputSmall, { width: 60 }]}
                        value={editFormData.state || ''}
                        onChangeText={(value) => handleInputChange('state', value)}
                        placeholder="State"
                        maxLength={2}
                        autoCapitalize="characters"
                      />
                      <TextInput
                        style={[styles.editInput, styles.editInputSmall, { width: 80 }]}
                        value={editFormData.zip_code || ''}
                        onChangeText={(value) => handleInputChange('zip_code', value)}
                        placeholder="ZIP"
                        keyboardType="numeric"
                        maxLength={10}
                      />
                    </View>
                  </>
                ) : (
                  <>
                    <Text style={styles.homeAddress}>{selectedHome.address}</Text>
                    <Text style={styles.homeCityState}>
                      {selectedHome.city}, {selectedHome.state} {selectedHome.zip_code}
                    </Text>
                  </>
                )}
              </View>
              {!isEditing && <ChevronDown size={20} color="#6b7280" />}
            </Pressable>

            {!isEditing ? (
              <Pressable style={styles.editButton} onPress={handleEdit}>
                <Edit size={18} color="#6b7280" />
              </Pressable>
            ) : (
              <View style={styles.editActions}>
                <Pressable 
                  style={styles.cancelButton} 
                  onPress={handleCancelEdit}
                  disabled={saving}
                >
                  <X size={18} color="#6b7280" />
                </Pressable>
                <Pressable 
                  style={[styles.saveButton, saving && styles.saveButtonDisabled]} 
                  onPress={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={styles.saveButtonText}>Saving...</Text>
                    </>
                  ) : (
                    <>
                      <Check size={18} color="#ffffff" />
                      <Text style={styles.saveButtonText}>Save</Text>
                    </>
                  )}
                </Pressable>
              </View>
            )}
          </View>

          {/* Home Stats */}
          {isEditing ? (
            <View style={styles.editStatsGrid}>
              <View style={styles.editStatField}>
                <Text style={styles.editStatLabel}>Square Footage</Text>
                <TextInput
                  style={styles.editInput}
                  value={editFormData.square_footage?.toString() || ''}
                  onChangeText={(value) => handleInputChange('square_footage', value === '' ? null : (value as any))}
                  placeholder="sq ft"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.editStatField}>
                <Text style={styles.editStatLabel}>Bedrooms</Text>
                <TextInput
                  style={styles.editInput}
                  value={editFormData.bedrooms?.toString() || ''}
                  onChangeText={(value) => handleInputChange('bedrooms', value === '' ? null : (value as any))}
                  placeholder="beds"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.editStatField}>
                <Text style={styles.editStatLabel}>Bathrooms</Text>
                <TextInput
                  style={styles.editInput}
                  value={editFormData.bathrooms?.toString() || ''}
                  onChangeText={(value) => handleInputChange('bathrooms', value === '' ? null : (value as any))}
                  placeholder="baths"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.editStatField}>
                <Text style={styles.editStatLabel}>Year Built</Text>
                <TextInput
                  style={styles.editInput}
                  value={editFormData.year_built?.toString() || ''}
                  onChangeText={(value) => handleInputChange('year_built', value === '' ? null : (value as any))}
                  placeholder="built"
                  keyboardType="numeric"
                  maxLength={4}
                />
              </View>
            </View>
          ) : (
            <View style={styles.homeStats}>
              {selectedHome.square_footage && (
                <View style={styles.homeStat}>
                  <Text style={styles.homeStatValue}>
                    {selectedHome.square_footage.toLocaleString()}
                  </Text>
                  <Text style={styles.homeStatLabel}>sq ft</Text>
                </View>
              )}
              {selectedHome.bedrooms && (
                <View style={styles.homeStat}>
                  <Text style={styles.homeStatValue}>{selectedHome.bedrooms}</Text>
                  <Text style={styles.homeStatLabel}>beds</Text>
                </View>
              )}
              {selectedHome.bathrooms && (
                <View style={styles.homeStat}>
                  <Text style={styles.homeStatValue}>{selectedHome.bathrooms}</Text>
                  <Text style={styles.homeStatLabel}>baths</Text>
                </View>
              )}
              {selectedHome.year_built && (
                <View style={styles.homeStat}>
                  <Text style={styles.homeStatValue}>{selectedHome.year_built}</Text>
                  <Text style={styles.homeStatLabel}>built</Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Timeline Section */}
        <View style={styles.timelineContainer}>
          <Text style={styles.timelineTitle}>Timeline</Text>
          
          {timelineEvents.length === 0 ? (
            <View style={styles.emptyTimeline}>
              <Text style={styles.emptyTimelineText}>No activity yet</Text>
              <Text style={styles.emptyTimelineSubtext}>
                Events will appear here as you use HoneyDone
              </Text>
            </View>
          ) : (
            <View style={styles.timeline}>
              {timelineEvents.map((event, index) => (
                <View key={event.id} style={styles.timelineItem}>
                  {/* Timeline Line */}
                  {index !== timelineEvents.length - 1 && (
                    <View style={styles.timelineLine} />
                  )}
                  
                  {/* Event Icon */}
                  <View style={styles.timelineIconContainer}>
                    <event.icon size={16} color="#000000" />
                  </View>

                  {/* Event Content */}
                  <View style={styles.timelineContent}>
                    <View style={styles.timelineHeader}>
                      <Text style={styles.timelineEventTitle}>{event.title}</Text>
                      <Text style={styles.timelineDate}>
                        {event.status === 'upcoming' ? 'Upcoming' : formatDate(event.date)}
                      </Text>
                    </View>
                    
                    {event.description && (
                      <Text style={styles.timelineDescription}>
                        {event.description}
                      </Text>
                    )}

                    <View style={styles.timelineFooter}>
                      {event.status && event.type !== 'maintenance' && (
                        <View style={[
                          styles.statusBadge,
                          event.status === 'completed' && styles.statusBadgeCompleted,
                          event.status === 'in_progress' && styles.statusBadgeInProgress,
                          event.status === 'pending' && styles.statusBadgePending,
                          event.status === 'upcoming' && styles.statusBadgeUpcoming,
                        ]}>
                          <Text style={[
                            styles.statusBadgeText,
                            event.status === 'completed' && styles.statusBadgeTextCompleted,
                            event.status === 'in_progress' && styles.statusBadgeTextInProgress,
                            event.status === 'pending' && styles.statusBadgeTextPending,
                            event.status === 'upcoming' && styles.statusBadgeTextUpcoming,
                          ]}>
                            {event.status.replace('_', ' ')}
                          </Text>
                        </View>
                      )}
                      
                      {/* Show amount in footer for non-expense types */}
                      {event.amount !== undefined && event.amount !== null && event.type !== 'expense' && (
                        <Text style={styles.timelineAmount}>
                          {formatAmount(event.amount)}
                        </Text>
                      )}

                      {event.recordType && (
                        <View style={styles.recordTypeBadge}>
                           <Text style={styles.recordTypeBadgeText}>
                            {event.recordType.replace('_', ' ')}
                           </Text>
                        </View>
                      )}
                    </View>

                    {/* Show expense amount with styled container for expense types */}
                    {event.type === 'expense' && event.amount !== undefined && event.amount !== null && (
                      <View style={styles.expenseTotalContainer}>
                        <DollarSign size={14} color="#059669" />
                        <Text style={styles.expenseTotalLabel}>Total Expenses:</Text>
                        <Text style={styles.expenseTotalAmount}>
                          {formatAmount(event.amount)}
                        </Text>
                      </View>
                    )}

                    {/* Show expense total for service requests and visits */}
                    {(event.type === 'service_request' || event.type === 'visit') &&
                      event.expenseTotal !== undefined &&
                      event.expenseTotal > 0 && (
                      <View style={styles.expenseTotalContainer}>
                        <DollarSign size={14} color="#059669" />
                        <Text style={styles.expenseTotalLabel}>Total Expenses:</Text>
                        <Text style={styles.expenseTotalAmount}>
                          {formatAmount(event.expenseTotal)}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      </WebScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingHorizontal: 24,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  scrollView: {
    flex: 1,
  },
  coverPhotoContainer: {
    width: '100%',
    height: 200,
    backgroundColor: '#f3f4f6',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },
  coverPhoto: {
    width: '100%',
    height: '100%',
  },
  coverPhotoPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
  },
  uploadButton: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
    ...createShadow(0, 2, 0.1, 8, 5),
  },
  uploadButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#dc2626',
  },
  errorText: {
    color: '#991b1b',
    fontSize: 14,
    fontWeight: '500',
  },
  homeInfoContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  homeSwitcher: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    flex: 1,
  },
  homeSwitcherContent: {
    flex: 1,
  },
  homeAddress: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  homeCityState: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  switcherBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10,
  },
  switcherDropdown: {
    position: 'absolute',
    top: 216, // Cover photo height + some padding
    left: 16,
    right: 16,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...createShadow(0, 2, 0.1, 8, 5),
    zIndex: 20,
    maxHeight: 300,
  },
  switcherItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  switcherItemActive: {
    backgroundColor: '#f9fafb',
  },
  switcherItemText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  switcherItemTextActive: {
    color: '#000000',
    fontWeight: '600',
  },
  switcherItemSubtext: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  homeStats: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 24,
  },
  homeStat: {
    alignItems: 'center',
  },
  homeStatValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  homeStatLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  homeInfoHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  editButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  editActions: {
    flexDirection: 'row',
    gap: 8,
  },
  cancelButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#000000',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  editInput: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 16,
    backgroundColor: '#ffffff',
    color: '#111827',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  editInputSmall: {
    fontSize: 14,
  },
  addressRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  editStatsGrid: {
    marginTop: 16,
    gap: 16,
  },
  editStatField: {
    gap: 4,
  },
  editStatLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6b7280',
  },
  timelineContainer: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  timelineTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  emptyTimeline: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyTimelineText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6b7280',
  },
  emptyTimelineSubtext: {
    fontSize: 14,
    color: '#9ca3af',
    marginTop: 4,
    textAlign: 'center',
  },
  timeline: {
    paddingLeft: 8,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 24,
    position: 'relative',
  },
  timelineLine: {
    position: 'absolute',
    left: 15,
    top: 32,
    bottom: -24,
    width: 2,
    backgroundColor: '#e5e7eb',
  },
  timelineIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    zIndex: 1,
  },
  timelineContent: {
    flex: 1,
    paddingTop: 4,
  },
  timelineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  timelineEventTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
    marginRight: 8,
  },
  timelineDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  timelineDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  timelineFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
  },
  statusBadgeCompleted: {
    backgroundColor: '#d1fae5',
  },
  statusBadgeInProgress: {
    backgroundColor: '#dbeafe',
  },
  statusBadgePending: {
    backgroundColor: '#fef3c7',
  },
  statusBadgeUpcoming: {
    backgroundColor: '#e0e7ff',
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'capitalize',
  },
  statusBadgeTextCompleted: {
    color: '#065f46',
  },
  statusBadgeTextInProgress: {
    color: '#1e40af',
  },
  statusBadgeTextPending: {
    color: '#92400e',
  },
  statusBadgeTextUpcoming: {
    color: '#3730a3',
  },
  timelineAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  expenseTotalContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  expenseTotalLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
  },
  expenseTotalAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#059669',
  },
  recordTypeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
  },
  recordTypeBadgeText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
  },
});

export default HomeDetail;
